import _ from 'lodash';
import { 
  IFilter, 
  ITranscriptSearchFilter, 
  ITranscriptSearchFilterCondition,
  ITranscriptSearchFilterGroup, 
  ITranscriptSearchFilterOption,
  ShortCallsOptions, 
  TranscriptSearchFilterOptionTypes } from "@/models/filter.model";
import { TranscriptionQueryConjunctions, TranscriptionQueryOperators } from "@/models/transcription.model";
import { ISelectOption } from "@/models/shared.model";

export const FILTER_DIALOG_SECTIONS = [
  { id: "filter_inputs_section_0", label: "Call Essentials", isCcaaSFiltersMetricsHidden: true },
  { id: "filter_inputs_section_1", label: "Data Sources", isCcaaSFiltersMetricsHidden: true },
  { id: "filter_inputs_section_2", label: "Call Attributes" },
  { id: "filter_inputs_section_3", label: "Transcript Search", showOnPlaybackOnly: true },
  { id: "filter_inputs_section_4", label: "Outcomes & Successes", isCcaaSFiltersMetricsHidden: true  },
  { id: "filter_inputs_section_5", label: "AI Analysis" },
  { id: "filter_inputs_section_6", label: "Scorecards", isScorecardsFilterHidden: true },
  { id: "filter_inputs_section_7", label: "Topics & Objections" },
  { id: "filter_inputs_section_8", label: "Sentiment" },
  { id: "filter_inputs_section_9", label: "Contact Info", showOnPlaybackOnly: true  }
];

export const FILTER_DIALOG_OPTIONS = {
  campaignIds: { name: "campaignIds", dataSourceName: "campaigns", chipsLabel: "Campaign" },
  teamIds: { name: "teamIds", dataSourceName: "teams", chipsLabel: "Team" },
  userIds: { name: "userIds", dataSourceName: "users", chipsLabel: "User" },
  queueIds: { name: "queueIds", dataSourceName: "queues", chipsLabel: "Queue" },
  resultCodes: { name: "resultCodes", dataSourceName: "resultCodes", chipsLabel: "Outcome" },
  recordingLength: { name: "recordingLength", dataSourceName: "", chipsLabel: "Call Duration" },
  callDirections: { name: "callDirections", dataSourceName: "callDirections", chipsLabel: "Call Type" },
  lists: { name: "lists", dataSourceName: "lists", chipsLabel: "List" },
  shortCalls: { name: "shortCalls", dataSourceName: "shortCalls", label: "Has AI Analysis", chipsLabel: "AI Analysis" },
  isScored: { name: "isScored", dataSourceName: "", label: "Is Scored", chipsLabel: "Scorecard" },
  scorecardFilter: { name: "scorecardFilter", dataSourceName: "", chipsLabel: "Scorecard" },
  daysOfWeek: { name: "daysOfWeek", dataSourceName: "daysOfWeek", label: "Day of the Week", chipsLabel: "Days" },
  transcriptSearch: { name:"transcriptSearch", dataSourceName: "", chipsLabel: "Transcript" },
  isSuccess: { name:"isSuccess", dataSourceName: "isSuccess", label: "Show successful interactions only", chipsLabel: "Show success only" },
  firstName: { name:"firstName", dataSourceName: "firstName", chipsLabel: "First Name" },
  lastName: { name:"lastName", dataSourceName: "lastName", chipsLabel: "Last Name" },
  phoneNumber: { name:"phoneNumber", dataSourceName: "phoneNumber", chipsLabel: "Phone Number" },
  postalCode: { name:"postalCode", dataSourceName: "postalCode", chipsLabel: "Postcode" },
  referenceId: { name:"referenceId", dataSourceName: "referenceId", chipsLabel: "Reference" },
  topics: { name:"topics", dataSourceName: "topics", chipsLabel: "Topic" },
  overallSentiments: { name:"overallSentiments", dataSourceName: "overallSentiments", chipsLabel: "Dominant Sentiment" },
  peakSentiments: { name:"peakSentiments", dataSourceName: "peakSentiments", chipsLabel: "Peak Sentiment" },
  endSentiments: { name:"endSentiments", dataSourceName: "endSentiments", chipsLabel: "End Sentiment" },
  objections: { name:"objections", dataSourceName: "objections", chipsLabel: "Objection" },
  dataSupplierIds: { name: "dataSupplierIds", dataSourceName: "dataSuppliers", chipsLabel: "Data Supplier" },
};

export const DEFAULT_TRANSCRIPTION_SEARCH_CONJUNCTION: ISelectOption<TranscriptionQueryConjunctions> = {
  value: TranscriptionQueryConjunctions.and,
  name: "AND",
};

export const TRANSCRIPTION_SEARCH_CONJUNCTIONS: ISelectOption<string>[] = [
  DEFAULT_TRANSCRIPTION_SEARCH_CONJUNCTION,
  {
    value: TranscriptionQueryConjunctions.or,
    name: "OR",
  },
];

export const TRANSCRIPTION_SEARCH_OPERATORS: ISelectOption<TranscriptionQueryOperators>[] = [
  {
    value: TranscriptionQueryOperators.contains,
    name: "contains",
    tooltip: "Find transcripts that include this exact phrase."
  },
  {
    value: TranscriptionQueryOperators.notContains,
    name: "does not contain",
    tooltip: "Exclude transcripts that include this exact phrase"
  },
  {
    value: TranscriptionQueryOperators.like,
    name: "is like",
    tooltip: "Find transcripts that include this or slight spelling variations of this phrase"
  }
];

export const DEFAULT_TRANSCRIPTION_SEARCH_CONDITION: ITranscriptSearchFilterCondition = {
  operator: TranscriptionQueryOperators.contains,
  text: undefined,
};

export const DEFAULT_TRANSCRIPTION_SEARCH_OPTION: ITranscriptSearchFilterOption = {
  type: TranscriptSearchFilterOptionTypes.condition,
  value: _.cloneDeep(DEFAULT_TRANSCRIPTION_SEARCH_CONDITION),
};

export const DEFAULT_TRANSCRIPTION_SEARCH_VALUE: ITranscriptSearchFilter = {
  conjunction: DEFAULT_TRANSCRIPTION_SEARCH_CONJUNCTION.value,
  options: [
    _.cloneDeep(DEFAULT_TRANSCRIPTION_SEARCH_OPTION),
  ],
};

export const DEFAULT_IS_SCORED_OPTION: ISelectOption<boolean | undefined> = {
  value: undefined,
  name: 'All',
  sequence: 0
};

export const SCORECARD_SCORED_OPTIONS: ISelectOption<boolean | undefined>[] = [
   { value: undefined, name: 'All', sequence: 1 },
   { value: true, name: 'Scored only', sequence: 2 },
   { value: false, name: 'Unscored only', sequence: 3 }
];

export const DEFAULT_TRANSCRIPTION_SEARCH_GROUP: ITranscriptSearchFilterGroup = {
  conjunction: DEFAULT_TRANSCRIPTION_SEARCH_CONJUNCTION.value,
  conditions: [
    _.cloneDeep(DEFAULT_TRANSCRIPTION_SEARCH_CONDITION),
  ],
};

export const DEFAULT_SHORT_CALLS_OPTION: ISelectOption<ShortCallsOptions> = {
  value: ShortCallsOptions.all,
  name: "All",
};

export const SHORT_CALLS_OPTIONS: ISelectOption<ShortCallsOptions>[] = [
  DEFAULT_SHORT_CALLS_OPTION,
  {
    value: ShortCallsOptions.showOnlyAiProcessed,
    name: "Show only interactions with analytics",
  },
  {
    value: ShortCallsOptions.showOnlyAiNotProcessed,
    name: "Show only short interactions without analytics",
  },
];

export const DEFAULT_FILTER: IFilter = {
  campaignIds: undefined,
  teamIds: undefined,
  userIds: undefined,
  queueIds: undefined,
  resultCodes: undefined,
  recordingLength: undefined,
  callDirections: undefined,
  lists: undefined,
  daysOfWeek: undefined,
  shortCalls: DEFAULT_SHORT_CALLS_OPTION.value,
  transcriptSearch: _.cloneDeep(DEFAULT_TRANSCRIPTION_SEARCH_VALUE),
  isScored: undefined,
  scorecardFilter: undefined,
  isSuccess: undefined,
  firstName: undefined,
  lastName: undefined,
  phoneNumber: undefined,
  postalCode: undefined,
  referenceId: undefined,
  topics: undefined,
  overallSentiments: undefined,
  peakSentiments: undefined,
  endSentiments: undefined,
  objections: undefined,
  dataSupplierIds: undefined,
};

export const DEFAULT_FILTER_STATE = {
  id: "11111111-1111-1111-1111-111111111111",
  name: 'All Data',
  folderId: undefined,
  filter: _.cloneDeep(DEFAULT_FILTER),
  chips: [],
  isDefault: true,
};